// api.js — Reva Jewellers API Service
import { compressImage } from "../utils/imageCompressor";
import { endpoints } from "./endpoints";

const BASE_URL = "https://api-dev.revajewellers.ashtamtechnologies.com/api";
export const BASE_ORIGIN = "https://api-dev.revajewellers.ashtamtechnologies.com";
export const toAbsUrl = (path) =>
  !path ? null : path.startsWith("http") ? path : `${BASE_ORIGIN}/${path.replace(/^\//, "")}`;

/* ─── tiny request helper ─── */
/* ─── tiny request helper ─── */
async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;

  const rawToken = localStorage.getItem("authToken");
  const token = rawToken ? rawToken.replace(/^Bearer\s+/i, "") : null;

  const { headers: customHeaders, ...restOptions } = options;

  const res = await fetch(url, {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(customHeaders || {}),
    },
  });

  let data = null;

  try {
    const text = await res.text();


    if (text) {
      try {
        data = JSON.parse(text);

      } catch {
        data = { message: text };
      }
    }
  } catch {
    // truly empty body
  }



  if (!res.ok) {

    // Auto logout on 401
    if (res.status === 401) {
      if (!window.location.pathname.startsWith("/catalogue")) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/";
        return;
      }
      return null;
    }

    const err = new Error(
      data?.message ||
      data?.Message ||
      data?.error ||
      data?.title ||
      (data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : null) ||
      `Request failed (${res.status})`
    );

    err.response = { status: res.status, data };

    throw err;
  }

  return data;
}

function toQueryString(params) {
  if (!params) return "";
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value);
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export const api = {
  get: (path, options = {}) => {
    const qs = toQueryString(options.params);
    const config = { method: "GET" };
    if (options.headers) config.headers = options.headers;
    return request(`${path}${qs}`, config);
  },
  post: (path, body, options = {}) => {
    const config = {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body),
    };
    if (options?.headers) config.headers = options.headers;
    return request(path, config);
  },
  put: (path, body, options = {}) => {
    const config = {
      method: "PUT",
      body: body instanceof FormData ? body : JSON.stringify(body),
    };
    if (options?.headers) config.headers = options.headers;
    return request(path, config);
  },
  delete: (path, options = {}) => {
    const qs = toQueryString(options?.params);
    const config = { method: "DELETE" };
    if (options?.headers) config.headers = options.headers;
    return request(`${path}${qs}`, config);
  },
};

/* ─── decode JWT (no library needed) ─── */
function decodeJWT(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return null;
  }
}

/* ════════════════════════════════════════════
   AUTH SERVICE
════════════════════════════════════════════ */
export const apiService = {

  /* ── Login ── */
  loginUser: async ({ email, password }) => {
    const res = await fetch(`${BASE_URL}/Login/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (data?.forcePasswordChange === true) {
      return {
        forcePasswordChange: true,
        userId: data.userId ?? data.user?.id ?? null,
        email,
        oldPassword: password,
      };
    }

    const token = data?.token ?? data?.data?.token ?? data?.accessToken;
    if (!token) throw new Error("No token received from server.");

    const payload = decodeJWT(token);
    if (payload?.exp && Date.now() / 1000 > payload.exp) {
      throw new Error("Session expired. Please try again.");
    }

    localStorage.setItem("authToken", token.replace(/^Bearer\s+/i, ""));
    if (data?.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
    const userData = data?.user || data;

    localStorage.setItem("user", JSON.stringify(userData));

    const roleId = data?.roleId ?? data?.user?.roleId ?? payload?.roleId;
    return { ...data, roleId, token };
  },

  /* ── Forced / first-time password change ──
     Exact payload the backend expects:
     { email, oldPassword, newPassword }
  ── */
  changePassword: async ({ email, oldPassword, newPassword }) => {
    return request("/Login/change-password", {
      method: "POST",
      body: JSON.stringify({ email, oldPassword, newPassword }),
    });
  },

  /* ── Forgot password ── */
  forgotPassword: async (email) => {
    return request("/Login/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  /* ── Logout ── */
  logout: () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  },

  /* ── Get stored token ── */
  getToken: () => localStorage.getItem("authToken"),

  /* ── Check if logged in ── */
  isAuthenticated: () => {
    const token = localStorage.getItem("authToken");
    if (!token) return false;
    const payload = decodeJWT(token);
    if (!payload?.exp) return true;
    return Date.now() / 1000 < payload.exp;
  },
};

/* ════════════════════════════════════════════
   ATTRIBUTE MASTER API
════════════════════════════════════════════ */
export const attributeApi = {

  /* ── GET all attributes ──
     GET /api/AttributeMaster
  ── */
  getAll: () =>
    request("/AttributeMaster", { method: "GET" }),

  /* ── GET single attribute by ID ──
     GET /api/AttributeMaster/{id}
  ── */
  getById: (id) =>
    request(`/AttributeMaster/${id}`, { method: "GET" }),

  /* ── POST create attribute ──
     POST /api/AttributeMaster
     Body: { attributeID: 0, attributeName, attributeType, isActive }
  ── */
  create: ({ attributeName, attributeType, isActive = true }) =>
    request("/AttributeMaster", {
      method: "POST",
      body: JSON.stringify({
        attributeID: 0,
        attributeName: attributeName.trim(),
        attributeType: attributeType.trim(),
        isActive,
      }),
    }),

  /* ── PUT update attribute ──
     PUT /api/AttributeMaster
     Body: { attributeID, attributeName, attributeType, isActive }
  ── */
  update: ({ attributeID, attributeName, attributeType, isActive }) =>
    request("/AttributeMaster", {
      method: "PUT",
      body: JSON.stringify({
        attributeID,
        attributeName: attributeName.trim(),
        attributeType: attributeType.trim(),
        isActive,
      }),
    }),

  /* ── DELETE attribute by ID ──
     DELETE /api/AttributeMaster/{id}
  ── */
  remove: (id) =>
    request(`/AttributeMaster/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════════════
   PRODUCT CATEGORY API
   ─────────────────────────────────────────────
   Paste this block into your api.js file.
   All requests automatically use BASE_URL,
   so switching dev → UAT → prod is one-line:
     const BASE_URL = "https://your-new-host/api";
════════════════════════════════════════════ */

export const productCategoryApi = {

  /* ── GET all categories ──
     GET /api/ProductCategory
     Returns: array of category objects
  ── */
  getAll: () =>
    request("/ProductCategory", { method: "GET" }),

  /* ── GET single category by ID ──
     GET /api/ProductCategory/{id}
  ── */
  getById: (id) =>
    request(`/ProductCategory/${id}`, { method: "GET" }),

  /* ── POST create category ──
     POST /api/ProductCategory
     Body: {
       categoryID:      0,          // always 0 for new
       categoryName:    string,     // required
       parentCategoryID: number,    // 0 for top-level
       taxConfigID:     number,     // 0 if not applicable
       codeHSN:         string,     // HSN code, can be ""
     }
  ── */
  create: ({ categoryName, parentCategoryID = 0, taxConfigID = 0, codeHSN = "" }) =>
    request("/ProductCategory", {
      method: "POST",
      body: JSON.stringify({
        categoryID: 0,
        categoryName: categoryName.trim(),
        parentCategoryID: Number(parentCategoryID) || 0,
        taxConfigID: Number(taxConfigID) || 0,
        codeHSN: codeHSN.trim(),
      }),
    }),

  /* ── PUT update category ──
     PUT /api/ProductCategory
     Body: full category object with categoryID
  ── */
  update: ({ categoryID, categoryName, parentCategoryID = 0, taxConfigID = 0, codeHSN = "" }) =>
    request("/ProductCategory", {
      method: "PUT",
      body: JSON.stringify({
        categoryID,
        categoryName: categoryName.trim(),
        parentCategoryID: Number(parentCategoryID) || 0,
        taxConfigID: Number(taxConfigID) || 0,
        codeHSN: codeHSN.trim(),
      }),
    }),

  /* ── DELETE category by ID ──
     DELETE /api/ProductCategory/{id}
  ── */
  remove: (id) =>
    request(`/ProductCategory/${id}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   TAX CONFIG API
   ─────────────────────────────────────────────
   Paste this block into your api.js file,
   right after the productCategoryApi block.
   All requests use the shared BASE_URL and
   request() helper already defined in api.js,
   so switching dev → UAT → prod is one line:
     const BASE_URL = "https://your-new-host/api";
════════════════════════════════════════════ */

export const taxConfigApi = {

  /* ── GET all tax configs ──
     GET /api/TaxConfig
     Returns: flat array — all records including
     historical entries (same taxName, different effdt).
     The UI groups them client-side by taxName to build
     an effective-date history timeline.
  ── */
  getAll: () =>
    request("/TaxConfig", { method: "GET" }),

  /* ── GET single tax config by ID ──
     GET /api/TaxConfig/{id}
  ── */
  getById: (id) =>
    request(`/TaxConfig/${id}`, { method: "GET" }),

  /* ── POST create tax config ──
     POST /api/TaxConfig
     Body: {
       taxConfigID:   0,           // always 0 for new
       effdt:         "YYYY-MM-DD",// effective date (ISO date string)
       taxName:       string,      // required
       taxPercentage: number,      // 0–100
       isActive:      boolean,
     }
     Note: Changing effdt on an existing taxName
     creates a new history entry in the backend.
     The UI reflects this automatically on next fetch.
  ── */
  create: ({ taxName, taxPercentage, effdt, isActive = true }) =>
    request("/TaxConfig", {
      method: "POST",
      body: JSON.stringify({
        taxConfigID: 0,
        effdt,
        taxName: taxName.trim(),
        taxPercentage: Number(taxPercentage),
        isActive,
      }),
    }),

  /* ── PUT update tax config ──
     PUT /api/TaxConfig
     Body: full object with taxConfigID.
     If effdt is changed relative to the current record,
     this will create a new history entry server-side.
  ── */
  update: ({ taxConfigID, taxName, taxPercentage, effdt, isActive }) =>
    request("/TaxConfig", {
      method: "PUT",
      body: JSON.stringify({
        taxConfigID,
        effdt,
        taxName: taxName.trim(),
        taxPercentage: Number(taxPercentage),
        isActive,
      }),
    }),

  /* ── DELETE tax config by ID ──
     DELETE /api/TaxConfig/{id}
     Deletes a single record (one history entry).
     To remove all history for a taxName, call
     remove() for each entry's taxConfigID.
  ── */
  remove: (id, effdt) =>
    request(`/TaxConfig/${id}${effdt ? `?effdt=${effdt}` : ""}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   CATEGORY ATTRIBUTE MAP API
   ─────────────────────────────────────────────
   Add this block to api.js, after the
   taxConfigApi block and before the
   `export default apiService;` line.
════════════════════════════════════════════ */

export const categoryAttributeMapApi = {

  /* ── GET all mappings ──
     GET /api/CategoryAttributeMap
     Returns: array of map objects
     { mapID, categoryID, attributeID, isRequired }
  ── */
  getAll: () =>
    request("/CategoryAttributeMap", { method: "GET" }),

  /* ── GET single mapping by ID ──
     GET /api/CategoryAttributeMap/{id}
  ── */
  getById: (id) =>
    request(`/CategoryAttributeMap/${id}`, { method: "GET" }),

  /* ── POST create mapping ──
     POST /api/CategoryAttributeMap
     Body: {
       mapID:       0,       // always 0 for new
       categoryID:  number,  // required
       attributeID: number,  // required
       isRequired:  boolean,
     }
  ── */
  create: ({ categoryID, attributeID, isRequired = false }) =>
    request("/CategoryAttributeMap", {
      method: "POST",
      body: JSON.stringify({
        mapID: 0,
        categoryID: Number(categoryID),
        attributeID: Number(attributeID),
        isRequired,
      }),
    }),

  /* ── PUT update mapping ──
     PUT /api/CategoryAttributeMap
     Body: full object with mapID
  ── */
  update: ({ mapID, categoryID, attributeID, isRequired }) =>
    request("/CategoryAttributeMap", {
      method: "PUT",
      body: JSON.stringify({
        mapID,
        categoryID: Number(categoryID),
        attributeID: Number(attributeID),
        isRequired,
      }),
    }),

  /* ── DELETE mapping by mapID ──
     DELETE /api/CategoryAttributeMap/{mapID}
  ── */
  remove: (mapID) =>
    request(`/CategoryAttributeMap/${mapID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   VENDOR API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   categoryAttributeMapApi block (or after
   taxConfigApi) and before:
     export default apiService;
════════════════════════════════════════════ */

export const vendorApi = {

  /* ── GET all vendors ──
     GET /api/Vendor
     Returns: array of vendor objects
     { vendorID, vendorName, isInHouse,
       phoneNumber, contactNumber, address }
  ── */
  getAll: () =>
    request("/Vendor", { method: "GET" }),

  /* ── GET single vendor by ID ──
     GET /api/Vendor/{id}
  ── */
  getById: (id) =>
    request(`/Vendor/${id}`, { method: "GET" }),

  /* ── POST create vendor ──
     POST /api/Vendor
     Body: {
       vendorID:      0,       // always 0 for new
       vendorName:    string,  // required
       isInHouse:     boolean,
       phoneNumber:   string,
       contactNumber: string,
       address:       string,
     }
  ── */
  create: ({
    vendorName,
    isInHouse = false,
    phoneNumber = "",
    contactNumber = "",
    address = "",
    bankName = "",
    bankAccountHolderName = "",
    bankAccountNumber = "",
    bankIFSCCode = "",
  }) =>
    request("/Vendor", {
      method: "POST",
      body: JSON.stringify({
        vendorID: 0,
        vendorName: vendorName.trim(),
        isInHouse,
        phoneNumber: phoneNumber.trim(),
        contactNumber: contactNumber.trim(),
        address: address.trim(),
        bankName: (bankName || "").trim(),
        bankAccountHolderName: (bankAccountHolderName || "").trim(),
        bankAccountNumber: (bankAccountNumber || "").trim(),
        bankIFSCCode: (bankIFSCCode || "").trim(),
      }),
    }),

  /* ── PUT update vendor ──
     PUT /api/Vendor
     Body: full object with vendorID
  ── */
  update: ({
    vendorID,
    vendorName,
    isInHouse,
    phoneNumber = "",
    contactNumber = "",
    address = "",
    bankName = "",
    bankAccountHolderName = "",
    bankAccountNumber = "",
    bankIFSCCode = "",
  }) =>
    request("/Vendor", {
      method: "PUT",
      body: JSON.stringify({
        vendorID,
        vendorName: vendorName.trim(),
        isInHouse,
        phoneNumber: phoneNumber.trim(),
        contactNumber: contactNumber.trim(),
        address: address.trim(),
        bankName: (bankName || "").trim(),
        bankAccountHolderName: (bankAccountHolderName || "").trim(),
        bankAccountNumber: (bankAccountNumber || "").trim(),
        bankIFSCCode: (bankIFSCCode || "").trim(),
      }),
    }),

  /* ── DELETE vendor by ID ──
     DELETE /api/Vendor/{vendorID}
  ── */
  remove: (vendorID) =>
    request(`/Vendor/${vendorID}`, { method: "DELETE" }),
};

/* ════════════════════════════════════════════
   PRODUCT API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   vendorApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const productApi = {

  /* ── GET all products ──
     GET /api/Product
     Returns: array of product objects
     {
       productID, productCode, productName,
       categoryID, vendorID, pricingType,
       purityStandard, barcodeType, isActive,
       codeHSNOverride, codeHSN,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/Product", { method: "GET" }),

  /* ── GET single product by ID ──
     GET /api/Product/{id}
  ── */
  getById: (id) =>
    request(`/Product/${id}`, { method: "GET" }),

  /* ── POST create product ──
     POST /api/Product
     Body: {
       productID:       0,          // always 0 for new
       productCode:     string,     // required
       productName:     string,     // required
       categoryID:      number,     // required
       vendorID:        number,     // required
       pricingType:     string,     // e.g. "Fixed", "Per Gram"
       purityStandard:  string,     // e.g. "22K", "925 Silver"
       barcodeType:     string,     // e.g. "QR Code", "Code128"
       isActive:        boolean,
       codeHSNOverride: boolean,    // true = use codeHSN below
       codeHSN:         string,     // HSN override value
       lastUPDTTM:      string,     // ISO datetime — auto-set below
       lastUPDTID:      number,     // 0 default
     }
  ── */
  create: ({
    productCode,
    productName,
    categoryID,
    vendorID,
    pricingType = "",
    purityStandard = "",
    barcodeType = "",
    isActive = true,
    codeHSNOverride = false,
    codeHSN = "",
  }) =>
    request("/Product", {
      method: "POST",
      body: JSON.stringify({
        productID: 0,
        productCode: productCode.trim(),
        productName: productName.trim(),
        categoryID: Number(categoryID),
        vendorID: Number(vendorID),
        pricingType: pricingType.trim(),
        purityStandard: purityStandard.trim(),
        barcodeType: barcodeType.trim(),
        isActive,
        codeHSNOverride,
        codeHSN: codeHSN.trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID: 0,
      }),
    }),

  /* ── PUT update product ──
     PUT /api/Product
     Body: full product object with productID
  ── */
  update: ({
    productID,
    productCode,
    productName,
    categoryID,
    vendorID,
    pricingType = "",
    purityStandard = "",
    barcodeType = "",
    isActive = true,
    codeHSNOverride = false,
    codeHSN = "",
    lastUPDTID = 0,
  }) =>
    request("/Product", {
      method: "PUT",
      body: JSON.stringify({
        productID,
        productCode: productCode.trim(),
        productName: productName.trim(),
        categoryID: Number(categoryID),
        vendorID: Number(vendorID),
        pricingType: pricingType.trim(),
        purityStandard: purityStandard.trim(),
        barcodeType: barcodeType.trim(),
        isActive,
        codeHSNOverride,
        codeHSN: codeHSN.trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE product by ID ──
     DELETE /api/Product/{productID}
  ── */
  remove: (productID) =>
    request(`/Product/${productID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   PRODUCT ATTRIBUTE DETAIL API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   productApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const productAttributeDtlApi = {

  /* ── GET all product attribute details ──
     GET /api/ProductAttributeDtl
     Returns: array of records
     {
       productAttributeID,
       productID,
       attributeID,
       attributeValueString,
       attributeValueDecimal,
       attributeValueInteger,
     }
  ── */
  getAll: () =>
    request("/ProductAttributeDtl", { method: "GET" }),

  /* ── GET single record by ID ──
     GET /api/ProductAttributeDtl/{id}
  ── */
  getById: (id) =>
    request(`/ProductAttributeDtl/${id}`, { method: "GET" }),

  /* ── POST create product attribute ──
     POST /api/ProductAttributeDtl
     Body: {
       productAttributeID:   0,        // always 0 for new
       productID:            number,   // required
       attributeID:          number,   // required
       attributeValueString: string,   // populate only the relevant field
       attributeValueDecimal: number,  // for decimal/float type attributes
       attributeValueInteger: number,  // for integer type attributes
     }
     Note: Only one of the three value fields should be
     populated at a time, based on the attribute's type.
     The other two should be sent as "" / 0 respectively.
  ── */
  create: ({
    productID,
    attributeID,
    attributeValueString = "",
    attributeValueDecimal = 0,
    attributeValueInteger = 0,
  }) =>
    request("/ProductAttributeDtl", {
      method: "POST",
      body: JSON.stringify({
        productAttributeID: 0,
        productID: Number(productID),
        attributeID: Number(attributeID),
        attributeValueString: String(attributeValueString ?? "").trim(),
        attributeValueDecimal: attributeValueDecimal !== "" && attributeValueDecimal !== null
          ? Number(attributeValueDecimal) : 0,
        attributeValueInteger: attributeValueInteger !== "" && attributeValueInteger !== null
          ? Number(attributeValueInteger) : 0,
      }),
    }),

  /* ── PUT update product attribute ──
     PUT /api/ProductAttributeDtl
     Body: full object with productAttributeID
  ── */
  update: ({
    productAttributeID,
    productID,
    attributeID,
    attributeValueString = "",
    attributeValueDecimal = 0,
    attributeValueInteger = 0,
  }) =>
    request("/ProductAttributeDtl", {
      method: "PUT",
      body: JSON.stringify({
        productAttributeID,
        productID: Number(productID),
        attributeID: Number(attributeID),
        attributeValueString: String(attributeValueString ?? "").trim(),
        attributeValueDecimal: attributeValueDecimal !== "" && attributeValueDecimal !== null
          ? Number(attributeValueDecimal) : 0,
        attributeValueInteger: attributeValueInteger !== "" && attributeValueInteger !== null
          ? Number(attributeValueInteger) : 0,
      }),
    }),

  /* ── DELETE product attribute by ID ──
     DELETE /api/ProductAttributeDtl/{productAttributeID}
  ── */
  remove: (productAttributeID) =>
    request(`/ProductAttributeDtl/${productAttributeID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   INVENTORY SUMMARY API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   productAttributeDtlApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const inventorySummaryApi = {

  /* ── GET all inventory entries ──
     GET /api/InventorySummary
     Returns: array of stock objects
     {
       stockID, productID, quantity,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/InventorySummary", { method: "GET" }),

  /* ── GET single inventory entry by stockID ──
     GET /api/InventorySummary/{stockID}
  ── */
  getById: (stockID) =>
    request(`/InventorySummary/${stockID}`, { method: "GET" }),

  /* ── POST create inventory entry ──
     POST /api/InventorySummary
     Body: {
       stockID:    0,          // always 0 for new
       productID:  number,     // required — links to Product
       quantity:   number,     // required — current stock count
       lastUPDTTM: string,     // ISO datetime — auto-set below
       lastUPDTID: number,     // 0 default
     }
  ── */
  create: ({ productID, quantity, lastUPDTID = 0 }) =>
    request("/InventorySummary", {
      method: "POST",
      body: JSON.stringify({
        stockID: 0,
        productID: Number(productID),
        quantity: Number(quantity),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update inventory entry ──
     PUT /api/InventorySummary
     Body: full object with stockID
  ── */
  update: ({ stockID, productID, quantity, lastUPDTID = 0 }) =>
    request("/InventorySummary", {
      method: "PUT",
      body: JSON.stringify({
        stockID,
        productID: Number(productID),
        quantity: Number(quantity),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE inventory entry by stockID ──
     DELETE /api/InventorySummary/{stockID}
  ── */
  remove: (stockID) =>
    request(`/InventorySummary/${stockID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   INVENTORY ITEM DTL API  — FULL CRUD
   ─────────────────────────────────────────────
   Replace the existing inventoryItemDtlApi
   block in api.js (the one that only had
   getAll and getById) with this full version.
════════════════════════════════════════════ */

export const inventoryItemDtlApi = {

  /* ── GET all inventory item details ──
     GET /api/InventoryItemDtl
  ── */
  getAll: () =>
    request("/InventoryItemDtl", { method: "GET" }),

  /* ── GET single item by ID ──
     GET /api/InventoryItemDtl/{id}
  ── */
  getById: (id) =>
    request(`/InventoryItemDtl/${id}`, { method: "GET" }),

  /* ── POST create inventory item ──
     POST /api/InventoryItemDtl
     Body: {
       inventoryItemID: 0,          // always 0 for new
       stockID:         number,     // 0 if not linked
       productID:       number,     // required
       receiptLineID:   number,     // 0 if not applicable
       grossWeight:     number,     // in grams
       lessWeight:      number,     // in grams
       netWeight:       number,     // grossWeight - lessWeight
       quantity:        number,     // required
       barcodeID:       number,     // 0 if not assigned
       lastUPDTTM:      string,     // ISO datetime — auto-set
       lastUPDTID:      number,     // 0 default
     }
  ── */
  create: ({
    stockID = 0,
    productID,
    receiptLineID = 0,
    grossWeight = 0,
    lessWeight = 0,
    netWeight = 0,
    quantity,
    barcodeID = 0,
    lastUPDTID = 0,
  }) =>
    request("/InventoryItemDtl", {
      method: "POST",
      body: JSON.stringify({
        inventoryItemID: 0,
        stockID: Number(stockID) || 0,
        productID: Number(productID),
        receiptLineID: Number(receiptLineID) || 0,
        grossWeight: parseFloat(grossWeight) || 0,
        lessWeight: parseFloat(lessWeight) || 0,
        netWeight: parseFloat(netWeight) || 0,
        quantity: Number(quantity),
        barcodeID: Number(barcodeID) || 0,
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update inventory item ──
     PUT /api/InventoryItemDtl
     Body: full object with inventoryItemID
  ── */
  update: ({
    inventoryItemID,
    stockID = 0,
    productID,
    receiptLineID = 0,
    grossWeight = 0,
    lessWeight = 0,
    netWeight = 0,
    quantity,
    barcodeID = 0,
    lastUPDTID = 0,
  }) =>
    request("/InventoryItemDtl", {
      method: "PUT",
      body: JSON.stringify({
        inventoryItemID,
        stockID: Number(stockID) || 0,
        productID: Number(productID),
        receiptLineID: Number(receiptLineID) || 0,
        grossWeight: parseFloat(grossWeight) || 0,
        lessWeight: parseFloat(lessWeight) || 0,
        netWeight: parseFloat(netWeight) || 0,
        quantity: Number(quantity),
        barcodeID: Number(barcodeID) || 0,
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE inventory item by ID ──
     DELETE /api/InventoryItemDtl/{inventoryItemID}
  ── */
  remove: (inventoryItemID) =>
    request(`/InventoryItemDtl/${inventoryItemID}`, { method: "DELETE" }),
};




/* ════════════════════════════════════════════
   INVENTORY TRANSACTION API
   ─────────────────────────────────────────────
   Paste this block into api.js, after the
   inventoryItemDtlApi block and before:
     export default apiService;
════════════════════════════════════════════ */

export const inventoryTransactionApi = {

  /* ── GET all transactions ──
     GET /api/InventoryTransactionDtl
     Returns: array of transaction objects
     {
       transactionID, inventoryItemID, stockID,
       productID, transactionType, quantity,
       comments, lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/InventoryTransactionDtl", { method: "GET" }),

  /* ── GET single transaction by ID ──
     GET /api/InventoryTransactionDtl/{id}
  ── */
  getById: (id) =>
    request(`/InventoryTransactionDtl/${id}`, { method: "GET" }),

  /* ── POST create transaction ──
     POST /api/InventoryTransactionDtl
     Body: {
       transactionID:   0,        // always 0 for new
       inventoryItemID: number,   // from InventoryItem
       stockID:         number,   // from InventorySummary
       productID:       number,   // required — from Product
       transactionType: string,   // "Stock In" | "Stock Out" | "Adjustment" | "Damage"
       quantity:        number,   // required
       comments:        string,   // optional notes
       lastUPDTTM:      string,   // ISO datetime — auto-set below
       lastUPDTID:      number,   // 0 default
     }
  ── */
  create: ({
    inventoryItemID = 0,
    stockID = 0,
    productID,
    transactionType,
    quantity,
    comments = "",
    lastUPDTID = 0,
  }) =>
    request("/InventoryTransactionDtl", {
      method: "POST",
      body: JSON.stringify({
        transactionID: 0,
        inventoryItemID: Number(inventoryItemID) || 0,
        stockID: Number(stockID) || 0,
        productID: Number(productID),
        transactionType,
        quantity: Number(quantity),
        comments: String(comments ?? "").trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update transaction ──
     PUT /api/InventoryTransactionDtl
     Body: full object with transactionID
  ── */
  update: ({
    transactionID,
    inventoryItemID = 0,
    stockID = 0,
    productID,
    transactionType,
    quantity,
    comments = "",
    lastUPDTID = 0,
  }) =>
    request("/InventoryTransactionDtl", {
      method: "PUT",
      body: JSON.stringify({
        transactionID,
        inventoryItemID: Number(inventoryItemID) || 0,
        stockID: Number(stockID) || 0,
        productID: Number(productID),
        transactionType,
        quantity: Number(quantity),
        comments: String(comments ?? "").trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE transaction by ID ──
     DELETE /api/InventoryTransactionDtl/{transactionID}
  ── */
  remove: (transactionID) =>
    request(`/InventoryTransactionDtl/${transactionID}`, { method: "DELETE" }),
};
/* ════════════════════════════════════════════
   CATALOGUE ITEM API
   ─────────────────────────────────────────────
   Paste after the existing API blocks in api.js.
   Uses the shared BASE_URL + request() helper —
   change BASE_URL once to move dev → UAT → prod.
════════════════════════════════════════════ */

export const catalogueItemApi = {

  /* ── GET all catalogue items ──
     GET /api/CatalogueItem
     Returns: array of catalogue item objects
     {
       catalogueItemID, productID,
       displayName, descriptionLbl,
       isFeatured, displaySection,
       isActive, description, comments,
       lastUPDTTM, lastUPDTID
     }
  ── */
  getAll: () =>
    request("/CatalogueItem", { method: "GET" }),

  /* ── GET single item by ID ──
     GET /api/CatalogueItem/{catalogueItemID}
  ── */

  // Inside cataloguePhotoApi, after getById:
  getByItemId: (catalogueItemID) =>
    request(`/CataloguePhoto/catalogue/${catalogueItemID}`, { method: "GET" }),

  /* ── POST create catalogue item ──
     POST /api/CatalogueItem
     Body: {
       catalogueItemID: 0,           // always 0 for new
       productID:       number,      // required
       displayName:     string,      // required
       descriptionLbl:  string,      // short label
       isFeatured:      boolean,
       displaySection:  string,      // e.g. "Homepage"
       isActive:        boolean,
       description:     string,      // full description
       comments:        string,      // internal notes
       lastUPDTTM:      string,      // ISO datetime — auto-set
       lastUPDTID:      number,      // 0 default
     }
  ── */
  create: ({
    productID,
    displayName,
    descriptionLbl = "",
    isFeatured = false,
    displaySection = "",
    isActive = true,
    description = "",
    comments = "",
    lastUPDTID = 0,
  }) =>
    request("/CatalogueItem", {
      method: "POST",
      body: JSON.stringify({
        catalogueItemID: 0,
        productID: Number(productID),
        displayName: displayName.trim(),
        descriptionLbl: descriptionLbl.trim(),
        isFeatured,
        displaySection: displaySection.trim(),
        isActive,
        description: description.trim(),
        comments: comments.trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── PUT update catalogue item ──
     PUT /api/CatalogueItem
     Body: full object with catalogueItemID
  ── */
  update: ({
    catalogueItemID,
    productID,
    displayName,
    descriptionLbl = "",
    isFeatured = false,
    displaySection = "",
    isActive = true,
    description = "",
    comments = "",
    lastUPDTID = 0,
  }) =>
    request("/CatalogueItem", {
      method: "PUT",
      body: JSON.stringify({
        catalogueItemID,
        productID: Number(productID),
        displayName: displayName.trim(),
        descriptionLbl: descriptionLbl.trim(),
        isFeatured,
        displaySection: displaySection.trim(),
        isActive,
        description: description.trim(),
        comments: comments.trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID,
      }),
    }),

  /* ── DELETE catalogue item by ID ──
     DELETE /api/CatalogueItem/{catalogueItemID}
  ── */
  remove: (catalogueItemID) =>
    request(`/CatalogueItem/${catalogueItemID}`, { method: "DELETE" }),
};


/* ════════════════════════════════════════════
   CATALOGUE PHOTO API
   ─────────────────────────────────────────────
   Uses multipart/form-data for upload (POST/PUT).
   NOTE: We do NOT use the shared request() helper
   here because that sets Content-Type: application/json.
   For multipart, we let the browser set the boundary
   automatically by NOT setting Content-Type at all.
════════════════════════════════════════════ */

export const cataloguePhotoApi = {

  /* ── GET all photos ──
     GET /api/CataloguePhoto
     Returns: flat array of all photo records.
     Filter client-side by catalogueItemID to get
     photos for a specific catalogue item.
     {
       photoID, catalogueItemID,
       photos,           // array of file URLs or base64
       filepath, filename,
       comments, displayOrder, isPrimary
     }
  ── */
  getAll: () =>
    request("/CataloguePhoto", { method: "GET" }),

  /* ── GET single photo by photoID ──
     GET /api/CataloguePhoto/{photoID}
  ── */
  getById: (photoID) =>
    request(`/CataloguePhoto/${photoID}`, { method: "GET" }),
  getByItemId: (catalogueItemID) =>
    request(`/CataloguePhoto/catalogue/${catalogueItemID}`, { method: "GET" }),

  /* ── POST upload a new photo ──
     POST /api/CataloguePhoto
     Content-Type: multipart/form-data (browser sets boundary)
     Fields:
       PhotoID          → "0"           (always 0 for new)
       CatalogueItemID  → string number
       Photos           → File object   (the image file)
       Filepath         → string        (can be "")
       Filename         → string        (file.name)
       Comments         → string        (caption / note)
       DisplayOrder     → string number
       IsPrimary        → "true"/"false"
  ── */
  upload: async ({
    catalogueItemID,
    files,
    comments = "",
    displayOrder = 1,
    isPrimary = false,
  }) => {
    const token = localStorage.getItem("authToken");
    const rawFiles = Array.from(files);
    const compressedFiles = await Promise.all(
      rawFiles.map((f) => compressImage(f))
    );

    const uploadedRecords = [];

    // Process each file with POST followed by PUT for unique backend GUID isolation
    for (let i = 0; i < compressedFiles.length; i++) {
      const file = compressedFiles[i];
      const ord = Number(displayOrder) + i;
      const prim = i === 0 && isPrimary;

      // 1. Initial creation
      const postFd = new FormData();
      postFd.append("PhotoID", "0");
      postFd.append("CatalogueItemID", String(catalogueItemID));
      postFd.append("file", file, file.name);
      postFd.append("Photos", file, file.name);
      postFd.append("Filepath", file.name);
      postFd.append("Filename", file.name);
      postFd.append("Comments", comments);
      postFd.append("DisplayOrder", String(ord));
      postFd.append("IsPrimary", String(prim));

      const postRes = await fetch(`${BASE_URL}/CataloguePhoto`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: postFd,
      });

      const postData = await postRes.json().catch(() => null);
      if (!postRes.ok) {
        throw new Error(
          postData?.message ||
          postData?.Message ||
          postData?.title ||
          `Upload failed (${postRes.status})`
        );
      }

      const createdItem = Array.isArray(postData) ? postData[0] : postData;
      const createdPhotoID = createdItem?.photoID ?? createdItem?.PhotoID;

      // 2. Immediate PUT with file & Photos to trigger backend GUID disk file creation
      if (createdPhotoID) {
        try {
          const putFd = new FormData();
          putFd.append("PhotoID", String(createdPhotoID));
          putFd.append("CatalogueItemID", String(catalogueItemID));
          putFd.append("file", file, file.name);
          putFd.append("Photos", file, file.name);
          putFd.append("Filepath", file.name);
          putFd.append("Filename", file.name);
          putFd.append("Comments", comments);
          putFd.append("DisplayOrder", String(ord));
          putFd.append("IsPrimary", String(prim));

          const putRes = await fetch(`${BASE_URL}/CataloguePhoto`, {
            method: "PUT",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: putFd,
          });
          const putData = await putRes.json().catch(() => null);
          if (putRes.ok && putData) {
            uploadedRecords.push(putData);
            continue;
          }
        } catch (e) {
          console.warn("PUT GUID isolation fallback:", e);
        }
      }

      uploadedRecords.push(createdItem);
    }

    return uploadedRecords;
  },

  /* ── PUT update photo metadata ──
     PUT /api/CataloguePhoto
  ── */
  update: ({
    photoID,
    catalogueItemID,
    file = null,
    filepath = "",
    filename = "",
    comments = "",
    displayOrder = 1,
    isPrimary = false,
  }) => {
    const token = localStorage.getItem("authToken");
    const fd = new FormData();
    const resolvedName = file ? filename || file.name : filename;
    const resolvedPath = filepath || resolvedName;

    fd.append("PhotoID", String(photoID));
    fd.append("CatalogueItemID", String(catalogueItemID));
    if (file) {
      fd.append("file", file, resolvedName);
      fd.append("Photos", file, resolvedName);
      fd.append("Filename", resolvedName);
    } else {
      fd.append("Filename", resolvedName);
    }
    fd.append("Filepath", resolvedPath);
    fd.append("Comments", comments);
    fd.append("DisplayOrder", String(displayOrder));
    fd.append("IsPrimary", String(isPrimary));

    return fetch(`${BASE_URL}/CataloguePhoto`, {
      method: "PUT",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: fd,
    }).then(async res => {
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          data?.message || data?.Message || data?.title ||
          `Update failed (${res.status})`
        );
      }
      return data;
    });
  },

  /* ── DELETE photo by photoID ──
     DELETE /api/CataloguePhoto/{photoID}
  ── */
  remove: (photoID) =>
    request(`/CataloguePhoto/${photoID}`, { method: "DELETE" }),
};

/* ════════════════════════════════════════════
   CATALOGUE CMS ADAPTER HELPER
════════════════════════════════════════════ */
export function adaptSection(s) {
  if (!s) return s;
  const secId = s.sectionID ?? s.id;
  const title = s.title || s.publicTitle || "";
  const slug =
    s.slug ||
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  return {
    ...s,
    sectionID: secId,
    id: secId,
    title,
    publicTitle: title,
    internalName: s.internalName || slug || title,
    slug,
    subtitle: s.subtitle ?? "",
    sectionType: s.sectionType || "Collection",
    layoutTemplate: s.layoutTemplate || "Grid",
    layoutType: s.layoutType || "grid",
    placement: s.placement || "HOMEPAGE",
    isPublished:
      typeof s.isPublished === "boolean"
        ? s.isPublished
        : s.isPublished === 1 || s.isPublished === "true" || s.isPublished === "1",
    isArchived: !!s.isArchived,
    coverImageUrl: s.coverImageUrl || null,
    products: Array.isArray(s.products) ? s.products : [],
  };
}

/* ════════════════════════════════════════════
   CATALOGUE SECTION API (CMS)
════════════════════════════════════════════ */
export const catalogueSectionApi = {
  adaptSection,

  /* ── GET all sections ── */
  getAll: async (params = "", options = {}) => {
    const query = typeof params === "string" ? params : params ? `?${new URLSearchParams(params).toString()}` : "";
    const res = await request(`/CatalogueSection${query}`, { method: "GET", ...options });
    const list = Array.isArray(res) ? res : res?.data ?? [];
    return list.map(adaptSection);
  },

  /* ── GET single section by ID ── */
  getById: async (id) => {
    const res = await request(`/CatalogueSection/${id}`, { method: "GET" });
    return adaptSection(res?.data ?? res);
  },

  /* ── POST create section ── */
  create: async (data) => {
    const title = (data.publicTitle || data.title || "").trim();
    const slug =
      data.slug ||
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    const allowedTypes = ["Collection", "Featured", "Banner"];
    const sectionType = allowedTypes.includes(data.sectionType) ? data.sectionType : "Collection";
    const allowedTemplates = ["Grid", "Carousel"];
    const layoutTemplate = allowedTemplates.includes(data.layoutTemplate) ? data.layoutTemplate : "Grid";
    const allowedLayoutTypes = ["grid", "carousel", "horizontal"];
    const layoutType = allowedLayoutTypes.includes(data.layoutType) ? data.layoutType : "grid";
    const body = {
      sectionID: 0,
      title,
      slug,
      sectionType,
      layoutTemplate,
      layoutType,
      subtitle: data.subtitle || null,
      coverImageUrl: data.coverImageUrl || null,
      isPublished: data.isPublished ?? true,
      isArchived: false,
    };
    const res = await request("/CatalogueSection", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return adaptSection(res?.data ?? res);
  },

  /* ── PUT update section ── */
  update: async (id, data) => {
    const title = (data.publicTitle || data.title || "").trim();
    const slug =
      data.slug ||
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    const allowedTypes = ["Collection", "Featured", "Banner"];
    const sectionType = allowedTypes.includes(data.sectionType) ? data.sectionType : "Collection";
    const allowedTemplates = ["Grid", "Carousel"];
    const layoutTemplate = allowedTemplates.includes(data.layoutTemplate) ? data.layoutTemplate : "Grid";
    const allowedLayoutTypes = ["grid", "carousel", "horizontal"];
    const layoutType = allowedLayoutTypes.includes(data.layoutType) ? data.layoutType : "grid";
    const body = {
      sectionID: Number(id),
      title,
      slug,
      sectionType,
      layoutTemplate,
      layoutType,
      subtitle: data.subtitle || null,
      coverImageUrl: data.coverImageUrl || null,
      isPublished: data.isPublished ?? true,
      isArchived: data.isArchived ?? false,
    };
    const res = await request(`/CatalogueSection/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    return adaptSection(res?.data ?? res);
  },

  /* ── DELETE / archive section ── */
  archive: (id) =>
    request(`/CatalogueSection/${id}`, { method: "DELETE" }),

  /* ── GET products mapped to section ── */
  getProducts: async (sectionId) => {
    const res = await request(`/CatalogueSection/${sectionId}/products`, {
      method: "GET",
    });
    const list = Array.isArray(res) ? res : res?.data ?? [];
    return list.map((m, idx) => ({
      mappingID: m.mappingID ?? m.id,
      sectionID: Number(sectionId),
      catalogueItemID: m.catalogueItemID ?? m.id,
      displayOrder: m.displayOrder ?? idx + 1,
      badgeText: m.badgeText || null,
      isActive: m.isActive !== false,
      product: m.product || null,
    }));
  },

  /* ── POST assign product to section ── */
  assignProduct: async (sectionId, catalogueItemID, badgeText = null) => {
    const res = await request(`/CatalogueSection/${sectionId}/products`, {
      method: "POST",
      body: JSON.stringify({
        catalogueItemIDs: [Number(catalogueItemID)],
        badgeText: badgeText || null,
      }),
    });
    const list = Array.isArray(res) ? res : res?.data ?? [];
    const found = list.find(
      (m) => Number(m.catalogueItemID) === Number(catalogueItemID)
    );
    return (
      found || {
        mappingID: Date.now(),
        sectionID: Number(sectionId),
        catalogueItemID: Number(catalogueItemID),
        displayOrder: list.length || 1,
        badgeText: badgeText || null,
        isActive: true,
      }
    );
  },

  /* ── DELETE remove product from section ── */
  removeProduct: (sectionId, catalogueItemID) =>
    request(`/CatalogueSection/${sectionId}/products/${catalogueItemID}`, {
      method: "DELETE",
    }),

  /* ── PUT reorder products within section ── */
  reorderProducts: (sectionId, mappings = []) => {
    const body = Array.isArray(mappings)
      ? mappings.map((m, idx) => ({
        mappingID: m.mappingID ?? m.id ?? Number(m),
        displayOrder: idx + 1,
      }))
      : [];
    return request(`/CatalogueSection/${sectionId}/products/reorder`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
};

/* ════════════════════════════════════════════
   PAGE LAYOUT API (CMS)
════════════════════════════════════════════ */
export const pageLayoutApi = {
  /* ── GET draft layout (returns ordered array of sectionIDs) ── */
  getDraft: async (pageSlug = "homepage") => {
    const res = await request(`/PageLayout/${pageSlug}/draft`, {
      method: "GET",
    });
    const list = Array.isArray(res) ? res : res?.data ?? [];
    const sorted = [...list].sort(
      (a, b) => (a.stackOrder ?? 0) - (b.stackOrder ?? 0)
    );
    return sorted.map((item) => Number(item.sectionID));
  },

  /* ── PUT save draft layout ── */
  saveDraft: (pageSlug = "homepage", orderedSectionIds = []) => {
    const body = orderedSectionIds.map((sid, idx) => ({
      sectionID: Number(sid),
      stackOrder: idx + 1,
      isVisible: true,
      pageSlug,
    }));
    return request(`/PageLayout/${pageSlug}/draft`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  /* ── POST publish draft layout ── */
  publish: (pageSlug = "homepage") =>
    request(`/PageLayout/${pageSlug}/publish`, { method: "POST" }),

  /* ── GET layout change history ── */
  getHistory: (pageSlug = "homepage") =>
    request(`/PageLayout/${pageSlug}/history`, { method: "GET" }),
};

/* ════════════════════════════════════════════
   PUBLIC SHOWROOM API (CMS)
════════════════════════════════════════════ */
export const publicShowroomApi = {
  /* ── GET public homepage showcase ── */
  getHomepage: async () => {
    try {
      const res = await request("/public/showroom/homepage", { method: "GET" });
      const list = Array.isArray(res) ? res : res?.data ?? [];
      if (list.length > 0) return list.map(adaptSection);
    } catch (err) {
      console.warn("Public showroom endpoint fallback:", err);
    }

    // Fallback: fetch published sections + layout stack directly
    const [sections, layoutIds] = await Promise.all([
      catalogueSectionApi.getAll().catch(() => []),
      pageLayoutApi.getDraft("homepage").catch(() => []),
    ]);

    const published = sections.filter((s) => s.isPublished && !s.isArchived);
    const ordered =
      layoutIds.length > 0
        ? [...published].sort(
          (a, b) => layoutIds.indexOf(a.sectionID) - layoutIds.indexOf(b.sectionID)
        )
        : published;

    return Promise.all(
      ordered.map(async (sec) => {
        const products = await catalogueSectionApi
          .getProducts(sec.sectionID)
          .catch(() => []);
        return {
          ...sec,
          products: products.sort(
            (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
          ),
        };
      })
    );
  },

  /* ── GET public collection details ── */
  getCollection: (slug, params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : "";
    return request(`/public/showroom/collection/${slug}${query}`, {
      method: "GET",
    });
  },
};
export const barcodeApi = {
  getAll: () => request("/Barcode", { method: "GET" }),
  getById: (id) => request(`/Barcode/${id}`, { method: "GET" }),
  getByInventoryItem: (inventoryItemId) =>
    request(`/Barcode/Inventory/${inventoryItemId}`, { method: "GET" }),
  create: (data) =>
    request("/Barcode", {
      method: "POST",
      body: JSON.stringify({ barcodeID: 0, ...data }),
    }),
  update: (data) =>
    request("/Barcode", { method: "PUT", body: JSON.stringify(data) }),
  remove: (id) => request(`/Barcode/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════
   PRODUCT PRICE API
════════════════════════════════════ */
export const productPriceApi = {
  getAll: () => request("/ProductPrice", { method: "GET" }),
  getById: (id) => request(`/ProductPrice/${id}`, { method: "GET" }),
  getActive: (productId) => request(`/ProductPrice/active/${productId}`),
  getByProductAndDate: (productId, effdt) =>
    request(`/ProductPrice/product/${productId}/effdt/${effdt}`, { method: "GET" }),
  create: ({ productID, effdt, landingCost = 0, marginPercent = 0, sellingPrice = 0, otherCharges = 0, grossSellingPrice = 0 }) =>
    request("/ProductPrice", {
      method: "POST",
      body: JSON.stringify({
        priceID: 0,
        productID: Number(productID),
        effdt,
        landingCost: Number(landingCost),
        marginPercent: Number(marginPercent),
        sellingPrice: Number(sellingPrice),
        otherCharges: Number(otherCharges),
        grossSellingPrice: Number(grossSellingPrice),
      }),
    }),
  update: (data) =>
    request("/ProductPrice", { method: "PUT", body: JSON.stringify(data) }),
  remove: (id) => request(`/ProductPrice/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════
   STOCK RECEIPT API
════════════════════════════════════ */
export const stockReceiptApi = {
  getAll: () => request("/StockReceipt", { method: "GET" }),
  getById: (id) => request(`/StockReceipt/${id}`, { method: "GET" }),
  create: ({ vendorID, receiptDate, comments = "" }) =>
    request("/StockReceipt", {
      method: "POST",
      body: JSON.stringify({
        receiptID: 0,
        vendorID: Number(vendorID),
        receiptDate,
        comments: comments.trim(),
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID: 0,
      }),
    }),
  update: (data) =>
    request("/StockReceipt", {
      method: "PUT",
      body: JSON.stringify({ ...data, lastUPDTTM: new Date().toISOString() }),
    }),
  remove: (id) => request(`/StockReceipt/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════
   STOCK RECEIPT DTL API
════════════════════════════════════ */
export const stockReceiptDtlApi = {
  getAll: () => request("/StockReceiptDtl", { method: "GET" }),
  getById: (id) => request(`/StockReceiptDtl/${id}`, { method: "GET" }),
  create: ({ receiptID, productID, quantity, unitWeight = 0, totalWeight = 0, comments = "" }) =>
    request("/StockReceiptDtl", {
      method: "POST",
      body: JSON.stringify({
        receiptLineID: 0,
        receiptID: Number(receiptID),
        productID: Number(productID),
        quantity: Number(quantity),
        unitWeight: parseFloat(unitWeight) || 0,
        totalWeight: parseFloat(totalWeight) || 0,
        comments: comments.trim(),
      }),
    }),
  update: (data) =>
    request("/StockReceiptDtl", {
      method: "PUT",
      body: JSON.stringify({
        receiptLineID: data.receiptLineID,
        receiptID: Number(data.receiptID),
        productID: Number(data.productID),
        quantity: Number(data.quantity),
        unitWeight: parseFloat(data.unitWeight) || 0,
        totalWeight: parseFloat(data.totalWeight) || 0,
        comments: data.comments || "",
      }),
    }),
  remove: (id) => request(`/StockReceiptDtl/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════
   INVOICE API
════════════════════════════════════ */
export const invoiceApi = {
  getAll: () => request("/Invoice", { method: "GET" }),
  getById: (id) => request(`/Invoice/${id}`, { method: "GET" }),
  create: ({ invoiceID = 0, invoiceNumber, invoiceDate, customerName, customerPhone = "", customerAddress = "", customerGSTIN = "", totalAmount = 0, totalTaxAmount = 0, grossAmount = 0, invoiceStatus = "Draft", lastUPDTTM, lastUPDID = 0 }) =>
    request("/Invoice", {
      method: "POST",
      body: JSON.stringify({
        invoiceID: 0,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        customerName: customerName.trim(),
        customerPhone: (customerPhone ?? "").trim(),
        customerAddress: (customerAddress ?? "").trim(),
        customerGSTIN: (customerGSTIN ?? "").trim(),
        totalAmount: Number(totalAmount),
        totalTaxAmount: Number(totalTaxAmount),
        grossAmount: Number(grossAmount),
        invoiceStatus,
        lastUPDTTM: lastUPDTTM ?? new Date().toISOString(),
        lastUPDID,
      }),
    }),
  update: (data) =>
    request("/Invoice", { method: "PUT", body: JSON.stringify(data) }),
  remove: (id) => request(`/Invoice/${id}`, { method: "DELETE" }),
};

/* ════════════════════════════════════
   INVOICE ITEM DTL API
════════════════════════════════════ */
export const invoiceItemDtlApi = {
  getAll: () => request("/InvoiceItemDtl", { method: "GET" }),
  getById: (id) => request(`/InvoiceItemDtl/${id}`, { method: "GET" }),
  create: ({ invoiceItemID = 0, invoiceID, productID, inventoryItemID = null, quantity, billedWeight = 0, unitPrice = 0, makingCharges = 0, lineAmount = 0, taxConfigID = 0, taxPercentage = 0, taxAmount = 0, totalPrice = 0, requestedSize = null, isCustomSize = false }) =>
    request("/InvoiceItemDtl", {
      method: "POST",
      body: JSON.stringify({
        invoiceItemID: 0,
        invoiceID: Number(invoiceID),
        productID: Number(productID),
        inventoryItemID: inventoryItemID ? Number(inventoryItemID) : null,
        quantity: Number(quantity),
        billedWeight: billedWeight && Number(billedWeight) > 0 ? Number(billedWeight) : null,
        unitPrice: Number(unitPrice) || 0,
        makingCharges: Number(makingCharges) || 0,
        lineAmount: Number(lineAmount) || 0,
        taxConfigID: Number(taxConfigID) || 0,
        taxPercentage: Number(taxPercentage) || 0,
        taxAmount: Number(taxAmount) || 0,
        totalPrice: Number(totalPrice) || 0,
        requestedSize: requestedSize != null && requestedSize !== "" ? Number(requestedSize) : null,
        isCustomSize: Boolean(isCustomSize),
      }),
    }),
  update: (data) =>
    request("/InvoiceItemDtl", {
      method: "PUT",
      body: JSON.stringify({
        ...data,
        inventoryItemID: data.inventoryItemID ? Number(data.inventoryItemID) : null,
        billedWeight: data.billedWeight && Number(data.billedWeight) > 0 ? Number(data.billedWeight) : null,
        makingCharges: Number(data.makingCharges) || 0,
        requestedSize: data.requestedSize != null && data.requestedSize !== "" ? Number(data.requestedSize) : null,
        isCustomSize: Boolean(data.isCustomSize),
      }),
    }),
  remove: (id) => request(`/InvoiceItemDtl/${id}`, { method: "DELETE" }),
};


/* ════════════════════════════════════════════
   NOTE API
════════════════════════════════════════════ */
export const noteApi = {
  getAll: (search = "") =>
    request(`/Note${search ? `?search=${encodeURIComponent(search)}` : ""}`, { method: "GET" }),

  getById: (id) =>
    request(`/Note/${id}`, { method: "GET" }),

  create: ({ title, contentDelta, contentHtml, plainText, referenceType = null, referenceID = null }) =>
    request("/Note", {
      method: "POST",
      body: JSON.stringify({
        noteID: 0,
        title: title.trim(),
        contentDelta,
        contentHtml,
        plainText: plainText ?? "",
        referenceType: referenceType || null,
        referenceID: referenceID ? Number(referenceID) : null,
        versionNo: 0,
        isActive: true,
        createdTTM: new Date().toISOString(),
        createdID: 0,
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID: 0,
      }),
    }),

  update: ({ noteID, title, contentDelta, contentHtml, plainText, versionNo, referenceType = null, referenceID = null }) =>
    request("/Note", {
      method: "PUT",
      body: JSON.stringify({
        noteID,
        title: title.trim(),
        contentDelta,
        contentHtml,
        plainText: plainText ?? "",
        referenceType: referenceType || null,
        referenceID: referenceID ? Number(referenceID) : null,
        versionNo,
        isActive: true,
        lastUPDTTM: new Date().toISOString(),
        lastUPDTID: 0,
      }),
    }),

  remove: (id) =>
    request(`/Note/${id}`, { method: "DELETE" }),

  getHistory: (id) =>
    request(`/Note/${id}/history`, { method: "GET" }),

  restore: (id, version) =>
    request(`/Note/${id}/restore/${version}`, { method: "POST" }),

  uploadImage: async (file) => {
    // Compress image to avoid HTTP 413 (Entity Too Large) & CORS preflight rejection
    const compressed = await compressImage(file, 1400, 0.82);
    const token = localStorage.getItem("authToken");
    const fd = new FormData();
    fd.append("file", compressed, compressed.name);

    return fetch(`${BASE_URL}/Note/upload-image`, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    }).then(async res => {
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          data?.message || data?.Message || data?.title ||
          `Upload failed (${res.status})`
        );
      }
      return data;
    });
  },
};

/* ════════════════════════════════════════════
   DASHBOARD APIS
════════════════════════════════════════════ */
export const dashboardSummaryApi = (params) =>
  api.get(endpoints.DASHBOARD.SUMMARY, { params });

export const dashboardRevenueByCategoryApi = (params) =>
  api.get(endpoints.DASHBOARD.REVENUE_BY_CATEGORY, { params });

export const dashboardTopProductsApi = (params) =>
  api.get(endpoints.DASHBOARD.TOP_PRODUCTS, { params });

export const dashboardStockWeightApi = () =>
  api.get(endpoints.DASHBOARD.STOCK_WEIGHT_BY_CATEGORY);

/* ════════════════════════════════════════════
   SALES REPORT APIS
════════════════════════════════════════════ */
export const reportSalesSummaryApi = (params) =>
  api.get(endpoints.REPORT.SALES_SUMMARY, { params });

export const reportSalesByCategoryApi = (params) =>
  api.get(endpoints.REPORT.SALES_BY_CATEGORY, { params });

export const reportSalesByProductApi = (params) =>
  api.get(endpoints.REPORT.SALES_BY_PRODUCT, { params });

export const reportInvoiceListApi = (params) =>
  api.get(endpoints.REPORT.INVOICE_LIST, { params });

export default apiService;